import React, { useEffect } from 'react';
import type { ActiveUserInfo } from '../types';

const getRankColor = (rank: string) => {
  switch (rank) {
    case 'GrandMaster': return { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' };
    case 'Master': return { bg: 'rgba(168, 85, 247, 0.2)', color: '#a855f7' };
    case 'Expert': return { bg: 'rgba(96, 165, 250, 0.2)', color: '#60a5fa' };
    case 'Professional': return { bg: 'rgba(250, 204, 21, 0.2)', color: '#facc15' };
    case 'Apprentice': return { bg: 'rgba(156, 163, 175, 0.2)', color: '#9ca3af' };
    case 'Beginner': return { bg: 'rgba(217, 119, 6, 0.2)', color: '#d97706' };
    default: return { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' };
  }
};

const modePills: { key: 'vsGames' | 'botGames' | 'singleGames' | 'dailyChallengeGames'; label: string; color: string; bg: string }[] = [
  { key: 'vsGames', label: 'VS Player', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.2)' },
  { key: 'botGames', label: 'VS Bot', color: '#fb923c', bg: 'rgba(251, 146, 60, 0.2)' },
  { key: 'singleGames', label: 'Single Player', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.2)' },
  { key: 'dailyChallengeGames', label: 'Daily Challenge', color: '#a855f7', bg: 'rgba(168, 85, 247, 0.2)' },
];

interface ActiveUsersModalProps {
  periodLabel: string;
  users: ActiveUserInfo[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  pageSize: number;
  onClose: () => void;
  onPageChange: (page: number) => void;
  onViewPlayer?: (userId: string) => void;
}

export const ActiveUsersModal: React.FC<ActiveUsersModalProps> = ({
  periodLabel,
  users,
  loading,
  error,
  totalCount,
  page,
  pageSize,
  onClose,
  onPageChange,
  onViewPlayer,
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalCount);

  const styles: Record<string, React.CSSProperties> = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      background: '#1e293b',
      borderRadius: '12px',
      width: '560px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 20px',
      borderBottom: '1px solid #334155',
    },
    title: {
      color: '#fff',
      fontSize: '16px',
      fontWeight: 700,
      margin: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: '#94a3b8',
      fontSize: '20px',
      cursor: 'pointer',
      padding: '4px 8px',
      lineHeight: 1,
    },
    body: {
      flex: 1,
      overflow: 'auto',
      padding: '12px 20px 20px',
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      padding: '10px 12px',
      margin: '4px 0',
      background: '#fff',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'background 0.15s',
      gap: '8px',
    },
    rankBadge: {
      padding: '4px 8px',
      borderRadius: '10px',
      fontSize: '11px',
      fontWeight: 600,
      flexShrink: 0,
      whiteSpace: 'nowrap',
      width: '90px',
      textAlign: 'center',
      boxSizing: 'border-box',
    },
    name: {
      fontWeight: 600,
      fontSize: '13px',
      color: '#111827',
      width: '140px',
      flexShrink: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    pillContainer: {
      display: 'flex',
      gap: '4px',
      flexWrap: 'wrap',
      flex: 1,
    },
    pill: {
      padding: '2px 6px',
      borderRadius: '8px',
      fontSize: '10px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
    spinner: {
      border: '3px solid rgba(255,255,255,0.3)',
      borderTop: '3px solid #2563eb',
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      animation: 'spin 1s linear infinite',
    },
    centered: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '150px',
    },
    errorText: {
      color: '#ef4444',
      fontSize: '14px',
      textAlign: 'center',
    },
    emptyText: {
      color: '#94a3b8',
      fontSize: '14px',
      textAlign: 'center',
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 20px',
      borderTop: '1px solid #334155',
    },
    footerText: {
      color: '#94a3b8',
      fontSize: '12px',
    },
    pageButton: {
      padding: '6px 12px',
      borderRadius: '4px',
      border: '1px solid #475569',
      background: 'transparent',
      color: '#e2e8f0',
      fontSize: '12px',
      fontWeight: 500,
      cursor: 'pointer',
    },
    pageButtonDisabled: {
      padding: '6px 12px',
      borderRadius: '4px',
      border: '1px solid #334155',
      background: 'transparent',
      color: '#475569',
      fontSize: '12px',
      fontWeight: 500,
      cursor: 'default',
    },
  };

  const hasAnyMode = (user: ActiveUserInfo) =>
    user.vsGames > 0 || user.botGames > 0 || user.singleGames > 0 || user.dailyChallengeGames > 0;

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h3 style={styles.title}>Active Users &mdash; {periodLabel}</h3>
          <button style={styles.closeButton} onClick={onClose}>&times;</button>
        </div>
        <div style={styles.body}>
          {loading ? (
            <div style={styles.centered}>
              <div style={styles.spinner} />
            </div>
          ) : error ? (
            <div style={styles.centered}>
              <p style={styles.errorText}>{error}</p>
            </div>
          ) : users.length === 0 ? (
            <div style={styles.centered}>
              <p style={styles.emptyText}>No active users found for this period</p>
            </div>
          ) : (
            users.map((user) => {
              const rankColors = getRankColor(user.rankName || '');
              return (
                <div
                  key={user.userId}
                  style={styles.row}
                  onClick={() => user.userId && onViewPlayer?.(user.userId)}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                >
                  <span style={{ ...styles.rankBadge, background: rankColors.bg, color: rankColors.color }}>
                    {user.rankName || 'Beginner'}
                  </span>
                  <span style={styles.name}>{user.username}</span>
                  <div style={styles.pillContainer}>
                    {hasAnyMode(user) ? (
                      modePills
                        .filter((mp) => user[mp.key] > 0)
                        .map((mp) => (
                          <span key={mp.key} style={{ ...styles.pill, background: mp.bg, color: mp.color }}>
                            {mp.label} ({user[mp.key]})
                          </span>
                        ))
                    ) : (
                      <span style={{ ...styles.pill, background: 'rgba(107,114,128,0.2)', color: '#6b7280' }}>
                        Online only
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        {totalCount > 0 && !loading && (
          <div style={styles.footer}>
            <span style={styles.footerText}>
              Showing {startItem}&ndash;{endItem} of {totalCount}
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                style={page <= 1 ? styles.pageButtonDisabled : styles.pageButton}
                disabled={page <= 1}
                onClick={() => page > 1 && onPageChange(page - 1)}
              >
                Previous
              </button>
              <button
                style={page >= totalPages ? styles.pageButtonDisabled : styles.pageButton}
                disabled={page >= totalPages}
                onClick={() => page < totalPages && onPageChange(page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
